import * as React from 'react'
import { memo } from 'react'
import StarIcon from '@mui/icons-material/Star'
import Rating from '@mui/material/Rating'
import Tooltip from '@mui/material/Tooltip'
import SocialIcon from 'components/SocialIcon'
import { format } from 'date-fns'
import {
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { FEED_TYPE_KEYWORD } from 'shared/constants'
import { parseUrlDomain } from 'shared/utils'
import numberToHuman from 'utils/format/numberToHuman'
import styles from './ContentCard.pcss'
import StatsBar from './StatsBar'

/* eslint-disable no-magic-numbers */
function calculateTimestamp(createdAt: string | Date | number): string {
  const now = Date.now()
  const createdAtMs = new Date(createdAt).getTime()
  const secondsDiff = Math.floor((now - createdAtMs) / 1000)

  // Less than 1 hour
  if (secondsDiff < 3600) {
    return 'just now'
  }

  // "new" filter: 0 to <24h - shows hours
  if (secondsDiff < 86400) {
    const hoursDiff = Math.floor(secondsDiff / 3600)
    return hoursDiff === 1 ? '1 hour ago' : `${hoursDiff} hours ago`
  }

  // "three_days" filter: 24h to <72h - shows 1-3 days
  // Use Math.ceil so items just past 24h show as "2 days" (entering day 2)
  if (secondsDiff < 259200) {
    const daysDiff = Math.ceil(secondsDiff / 86400)
    if (daysDiff === 1) return 'yesterday'
    return `${daysDiff} days ago`
  }

  // "week" filter: 72h to <168h - shows 4-7 days  
  // Use Math.ceil to ensure items just past 72h show as "4 days" not "3 days"
  if (secondsDiff < 604800) {
    const daysDiff = Math.ceil(secondsDiff / 86400)
    return `${daysDiff} days ago`
  }

  // "month" filter: 168h to <720h (7-30 days) - shows weeks
  // Use Math.ceil so items just past 168h (7 days) show as "2 weeks"
  if (secondsDiff < 2592000) {
    const weeksDiff = Math.ceil(secondsDiff / 604800)
    return weeksDiff === 1 ? '1 week ago' : `${weeksDiff} weeks ago`
  }

  // "year" filter: 720h to <8760h (30-365 days) - shows months
  // Use Math.ceil so items just past 720h (30 days) show as "2 months"
  if (secondsDiff < 31536000) {
    const monthsDiff = Math.ceil(secondsDiff / 2592000)
    return monthsDiff === 1 ? '1 month ago' : `${monthsDiff} months ago`
  }

  // "all" filter: 8760h+ (365+ days) - shows years
  const yearsDiff = Math.ceil(secondsDiff / 31536000)
  return yearsDiff === 1 ? '1 year ago' : `${yearsDiff} years ago`
}
/* eslint-enable no-magic-numbers */

const STATS_FEED_TYPES = ['facebook', 'twitter', 'reddit', 'bluesky']

export interface ContentCardFooterProps {
  content: ContentItem
  ref?: React.Ref<HTMLDivElement>
}

export function ContentCardFooter({ content, ref }: ContentCardFooterProps) {
  const type = content.type
  const feedType = content.feed.type
  const link = content.socialLink
  const title = (content as Article).title || (content as Video).description
  const isArticle = type === ARTICLE_TYPE
  const isVideo = type === VIDEO_TYPE
  const showStats = !isArticle && STATS_FEED_TYPES.includes(feedType)
  const showStarRating = !content.isStock
  // const withSharesByNetwork = isArticle && Object.keys((content as Article).sharesPerNetwork).length > 0
  const timestamp = calculateTimestamp(content.createdAt)
  const dateTime = new Date(content.createdAt).toISOString()
  const dateTimeString = format(content.createdAt, 'MMM d, yyyy')

  return (
    <div ref={ref} className={`${styles.footer} ${isVideo ? styles.video : ''}`}>
      {isArticle && (
        <p className={`${styles['source-link']} text-ellipsis`}>
          <a
            href={(content as Article).sourceLink}
            target="_blank"
            rel="noopener"
          >
            {parseUrlDomain((content as Article).sourceLink)}
          </a>
        </p>
      )}

      {(isArticle || isVideo) && title && (
        <h5 className={styles.title}>
          <a href={link} target="_blank" rel="noopener" className={styles['title-link']}>
            {title}
          </a>
        </h5>
      )}

      <div className={styles['footer-actions']} data-card-actions>
        {(isArticle || showStarRating) && (
          <div className={`${styles.col} ${styles.left}`}>
            <Tooltip title={dateTimeString}>
              <time
                key="timestamp"
                dateTime={dateTime}
                className={styles.timestamp}
              >
                {timestamp}
              </time>
            </Tooltip>
            {showStarRating && (
              <div className={styles['star-rating']}>
                <Rating
                  key="rating"
                  classes={{ icon: styles['rating-icon'] }}
                  readOnly
                  value={content.rating}
                  size="small"
                  emptyIcon={(
                    <StarIcon fontSize="inherit" />
                  )}
                />
              </div>
            )}
          </div>
        )}

        <div className={`${styles['network-bar']} ${styles.col} ${styles.right}`}>
          {!content.isStock && (
            <a
              href={`/content/feeds/${content.feed.uniqueSource || content.feed.id}`}
              className={`${styles['feed-name']} text-ellipsis`}
            >
              {content.feed.type !== FEED_TYPE_KEYWORD
                && <SocialIcon icon={content.feed.type} size="12px" className={styles['feed-network-icon']} />}
              <span className="text-ellipsis">{content.feed.name}</span>
            </a>
          )}
          {showStats && (
            <div className={styles['network-bar-stats']}>
              <StatsBar
                type={feedType}
                likes={content.likes}
                comments={content.comments}
                shares={content.shares}
              />
            </div>
          )}
          {isArticle && (
            <div className={styles['network-bar-stats']}>
              <span className={styles['total-shares']}>
                SHARES: {numberToHuman(content.shares)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(ContentCardFooter)
